import { useEffect, useState } from "react";
import PageHeader from "../components/PageHeader";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { Checkbox } from "../components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Label } from "../components/ui/label";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  Search,
  Plus,
  User,
  Edit,
  Trash2,
  Eye,
  Play,
  StopCircle,
  Loader2,
  Clock,
  MoreVertical,
  Settings,
  Key,
  Monitor,
  Clock as ClockIcon,
} from "lucide-react";
import {
  getEmployees,
  createEmployee,
  updateEmployee,
  deleteEmployee,
  getEmployeeById,
  seedEmployees,
  updateEmployeeStartStop,
  getActivitySetting,
  updateActivitySetting,
} from "../api/moniter";
import { getEmployees as getAllOrgEmployees } from "../api/employees";
import { getOffice } from "../api/officeandcompany";
import { getManagerList, getDepartmentList } from "../redux/slices/employee";
import { User as UserType } from "../types";
import { useToast } from "../hooks/useToast";
import { useAuth } from "../contexts/AuthContext";
import { dispatch, useSelector } from "../redux/store";

// Build a short, timezone-safe "30 Jun – 02 Jul" label from the overtime date
// range (YYYY-MM-DD strings the backend returns). Returns "" when no range is
// set so callers can decide whether to render brackets at all. Dates are parsed
// component-wise to avoid the UTC-midnight off-by-one that `new Date("YYYY-MM-DD")`
// can cause in negative-offset timezones.
const formatOvertimeRange = (emp: any): string => {
  const fmt = (d?: string) => {
    if (!d) return "";
    const [y, m, day] = String(d).slice(0, 10).split("-").map(Number);
    if (!y || !m || !day) return "";
    return new Date(y, m - 1, day).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
    });
  };
  const s = fmt(emp?.overtime_start_date);
  const e = fmt(emp?.overtime_end_date);
  if (!s && !e) return "";
  if (s && e) return s === e ? s : `${s} – ${e}`;
  return s || e;
};

export function ScreenRecording() {
  const [employees, setEmployees] = useState<UserType[]>([]);
  const [allOrgEmployees, setAllOrgEmployees] = useState<UserType[]>([]);
  const [filteredEmployees, setFilteredEmployees] = useState<UserType[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [departmentFilter, setDepartmentFilter] = useState("all");
  const [roleFilter, setRoleFilter] = useState("all");
  const [selectedEmployee, setSelectedEmployee] = useState<UserType | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showActivitySettingModal, setShowActivitySettingModal] = useState(false);
  const [activitySetting, setActivitySetting] = useState({ activityMinutes: 15 });
  const [activitySettingLoading, setActivitySettingLoading] = useState(false);
  const [loadingStates, setLoadingStates] = useState<{
    [key: string]: { startStop?: boolean; overtime?: boolean; delete?: boolean };
  }>({});
  const [overtimeDialogEmp, setOvertimeDialogEmp] = useState<string | null>(null);
  const todayStr = new Date().toISOString().split("T")[0];
  const [otStartDate, setOtStartDate] = useState<string>(todayStr);
  const [otEndDate, setOtEndDate] = useState<string>(todayStr);
  const { toast } = useToast();
  const { user } = useAuth();

  const canManageEmployees = user?.role === "admin" || user?.role === "hr";

  const departmentListMain = useSelector((state: any) => state.employee.departmentList) || [];

  useEffect(() => {
    fetchEmployees();
    fetchAllOrgEmployees();
    fetchActivitySetting();
    dispatch(getDepartmentList());
  }, []);

  useEffect(() => {
    filterEmployees();
  }, [employees, searchQuery, departmentFilter, roleFilter]);

  const fetchEmployees = async () => {
    try {
      const response = await getEmployees();
      setEmployees(response.monitors || []);
    } catch (error) {
      toast({ title: "Error", description: error.message || "Failed to load employees", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  // Full org roster — used to populate the Create Schedule employee dropdown.
  const fetchAllOrgEmployees = async () => {
    try {
      const response = await getAllOrgEmployees();
      setAllOrgEmployees(response.employees || []);
    } catch (error: any) {
      console.error("Error fetching org employees:", error);
    }
  };

  const fetchActivitySetting = async () => {
    try {
      const response = await getActivitySetting();
      if (response?.data?.activityMinutes) {
        setActivitySetting({ activityMinutes: response.data.activityMinutes });
      }
    } catch (error) {
      console.error("Error fetching activity setting:", error);
    }
  };

  const handleSaveActivitySetting = async () => {
    try {
      setActivitySettingLoading(true);
      await updateActivitySetting(activitySetting);
      toast({ title: "Success", description: "Activity setting updated successfully" });
      setShowActivitySettingModal(false);
    } catch (error) {
      toast({ title: "Error", description: error.message || "Failed to update activity setting", variant: "destructive" });
    } finally {
      setActivitySettingLoading(false);
    }
  };

  const filterEmployees = () => {
    let filtered = employees;
    if (searchQuery) {
      filtered = filtered.filter(
        (emp) =>
          emp.employeeName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          emp.employeeID?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          emp.loginID?.toLowerCase().includes(searchQuery.toLowerCase()),
      );
    }
    if (departmentFilter !== "all")
      filtered = filtered.filter(
        (emp: any) =>
          emp.department_id === departmentFilter ||
          emp.department === departmentFilter,
      );
    if (roleFilter !== "all") filtered = filtered.filter((emp) => emp.role === roleFilter);
    setFilteredEmployees(filtered);
  };

  const handleAddEmployee = async (employeeData: Partial<UserType>) => {
    try {
      await createEmployee(employeeData);
      toast({ title: "Success", description: "Employee added successfully" });
      setShowAddModal(false);
      fetchEmployees();
    } catch (error) {
      toast({ title: "Error", description: "Employee already exist" });
    }
  };

  const handleEditEmployee = async (employeeData: Partial<UserType>) => {
    if (!selectedEmployee) return;
    try {
      await updateEmployee(selectedEmployee.id, employeeData);
      toast({ title: "Success", description: "Employee updated successfully" });
      setShowEditModal(false);
      setSelectedEmployee(null);
      fetchEmployees();
    } catch (error) {
      toast({ title: "Error", description: error.message || "Failed to update employee", variant: "destructive" });
    }
  };

  const handleDeleteEmployee = async (id: string) => {
    try {
      setLoadingStates((prev) => ({ ...prev, [id]: { ...prev[id], delete: true } }));
      await deleteEmployee(id);
      toast({ title: "Success", description: "Employee deleted successfully" });
      fetchEmployees();
    } catch (error) {
      toast({ title: "Error", description: error.message || "Failed to delete employee", variant: "destructive" });
    } finally {
      setLoadingStates((prev) => ({ ...prev, [id]: { ...prev[id], delete: false } }));
    }
  };

  const handleViewEmployee = async (id: string) => {
    try {
      const employeeFromList = employees.find((emp) => emp.id === id);
      if (employeeFromList) {
        setSelectedEmployee(employeeFromList);
      } else {
        const response = await getEmployeeById(id);
        setSelectedEmployee(response.employee);
      }
      setShowViewModal(true);
    } catch (error) {
      toast({ title: "Error", description: error.message || "Failed to load employee details", variant: "destructive" });
    }
  };

  const handleStartStopEmployee = async (id: string, isStart: boolean, isOvertime: boolean) => {
    try {
      setLoadingStates((prev) => ({ ...prev, [id]: { ...prev[id], startStop: true } }));
      await updateEmployeeStartStop(id, !isStart, isOvertime);
      toast({ title: "Success", description: !isStart ? "Screen recording started" : "Screen recording stopped" });
      fetchEmployees();
    } catch (error) {
      toast({ description: error.message, variant: "destructive" });
    } finally {
      setLoadingStates((prev) => ({ ...prev, [id]: { ...prev[id], startStop: false } }));
    }
  };

  const handleOvertimeClick = async (employeeId: string, isOvertime: boolean) => {
    if (!isOvertime) {
      // Turning overtime ON → ask for the date range first.
      setOtStartDate(todayStr);
      setOtEndDate(todayStr);
      setOvertimeDialogEmp(employeeId);
      return;
    }
    // Turning overtime OFF → no range needed.
    try {
      setLoadingStates((prev) => ({ ...prev, [employeeId]: { ...prev[employeeId], overtime: true } }));
      await updateEmployeeStartStop(employeeId, true, false);
      toast({ title: "Success", description: "Overtime stopped for employee" });
      fetchEmployees();
    } catch (error: any) {
      toast({ description: error.message || "Failed to stop overtime", variant: "destructive" });
    } finally {
      setLoadingStates((prev) => ({ ...prev, [employeeId]: { ...prev[employeeId], overtime: false } }));
    }
  };

  const confirmStartOvertime = async () => {
    const employeeId = overtimeDialogEmp;
    if (!employeeId) return;
    if (otEndDate < otStartDate) {
      toast({ description: "End date can't be before start date", variant: "destructive" });
      return;
    }
    try {
      setLoadingStates((prev) => ({ ...prev, [employeeId]: { ...prev[employeeId], overtime: true } }));
      await updateEmployeeStartStop(employeeId, true, true, { start: otStartDate, end: otEndDate });
      toast({ title: "Success", description: "Overtime started for employee" });
      setOvertimeDialogEmp(null);
      fetchEmployees();
    } catch (error: any) {
      toast({ description: error.message || "Failed to start overtime", variant: "destructive" });
    } finally {
      setLoadingStates((prev) => ({ ...prev, [employeeId]: { ...prev[employeeId], overtime: false } }));
    }
  };

  // Canonical role list — always shown, regardless of who currently holds it.
  const roles = [
    "employee",
    "intern",
    "manager",
    "supervisor",
    "hr head",
    "hr admin",
    "director",
    "founder",
  ];

  // Employees who already have a schedule (the monitor list). Used to hide them
  // from the Create Schedule dropdown so a schedule isn't created twice.
  const scheduledEmployeeIDs = new Set(
    employees.map((e: any) => e.employeeID).filter(Boolean),
  );
  const availableEmployees = allOrgEmployees.filter(
    (e: any) => !scheduledEmployeeIDs.has(e.employeeID),
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4 p-3 sm:p-4 md:p-6">
      <PageHeader
        eyebrow="III. Work Monitor"
        title={<>Screen Recording <em>Summary</em></>}
        subtitle={<>Track your <em>team's</em> work schedule and on-screen activity.</>}
        actions={canManageEmployees ? (
          <>
            <Button
              onClick={() => setShowActivitySettingModal(true)}
              className="gt-btn is-primary"
            >
              <Settings className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1 sm:mr-2 flex-shrink-0" />
              <span className="whitespace-nowrap">Activity Configure</span>
            </Button>
            <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
              <DialogTrigger asChild>
                <Button className="gt-btn is-primary">
                  <Plus className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1 sm:mr-2 flex-shrink-0" />
                  <span className="whitespace-nowrap">Create Schedule</span>
                </Button>
              </DialogTrigger>
              <DialogContent className="w-[95vw] max-w-6xl bg-white max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>New Schedule</DialogTitle>
                  <DialogDescription>
                    Fill in the details to track your employee work
                  </DialogDescription>
                </DialogHeader>
                <EmployeeForm onSubmit={handleAddEmployee} employeeOptions={availableEmployees} />
              </DialogContent>
            </Dialog>
          </>
        ) : null}
      />
      {/* Activity Setting Modal */}
      <Dialog open={showActivitySettingModal} onOpenChange={setShowActivitySettingModal}>
        <DialogContent className="w-[95vw] max-w-md bg-white">
          <DialogHeader>
            <DialogTitle>Activity Settings</DialogTitle>
            <DialogDescription>
              Configure activity tracking settings for employees
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="activityMinutes">Activity Interval</Label>
              <Select
                value={String(activitySetting.activityMinutes)}
                onValueChange={(value) =>
                  setActivitySetting({ ...activitySetting, activityMinutes: parseInt(value) })
                }
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select activity interval" />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="5">5 minutes</SelectItem>
                  <SelectItem value="10">10 minutes</SelectItem>
                  <SelectItem value="15">15 minutes</SelectItem>
                  <SelectItem value="20">20 minutes</SelectItem>
                  <SelectItem value="30">30 minutes</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500 mt-1">
                Time gap between each data capture from Work Monitor
              </p>
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setShowActivitySettingModal(false)} className="text-sm">
              Cancel
            </Button>
            <Button
              onClick={handleSaveActivitySetting}
              disabled={activitySettingLoading}
              className="bg-gradient-to-r from-blue-500 to-purple-600 text-sm"
            >
              {activitySettingLoading ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Saving...</>
              ) : "Save"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Filters - fully stacked on mobile */}
      <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-lg">
        <CardContent className="p-3 sm:p-4 md:p-6">
          <div className="flex flex-col gap-3">
            {/* Search full width */}
            <div className="relative w-full">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <Input
                placeholder="Search employees..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 w-full"
              />
            </div>
            {/* Filters - side by side on mobile, row on desktop */}
            <div className="grid grid-cols-2 md:flex md:flex-row gap-2 md:gap-3">
              <Select value={departmentFilter} onValueChange={setDepartmentFilter}>
                <SelectTrigger className="w-full md:w-48 text-xs sm:text-sm">
                  <SelectValue placeholder="All Departments" />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="all">All Departments</SelectItem>
                  {departmentListMain.map((dept: any) => (
                    <SelectItem key={dept.id} value={dept.id}>
                      {dept.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={roleFilter} onValueChange={setRoleFilter}>
                <SelectTrigger className="w-full md:w-48 text-xs sm:text-sm">
                  <SelectValue placeholder="All Roles" />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="all">All Roles</SelectItem>
                  {roles.map((role) => (
                    <SelectItem key={role} value={role}>
                      {role.charAt(0).toUpperCase() + role.slice(1)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Employee Grid - 1 col mobile, 2 col tablet, 3 col desktop */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredEmployees.map((employee) => (
          <Card
            key={employee.id}
            className="bg-white/80 backdrop-blur-sm border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-[1.02]"
          >
            <CardHeader className="pb-2 pt-4 px-4">
              <div className="flex items-center justify-between">
                <Avatar className="h-10 w-10 sm:h-12 sm:w-12 flex-shrink-0">
                  <AvatarImage src={employee.profilePhoto} alt={employee.employeeName} />
                  <AvatarFallback className="text-sm">
                    {employee.employeeName?.charAt(0) || "E"}
                  </AvatarFallback>
                </Avatar>
                <div className="flex items-center gap-1.5">
                  <Badge
                    variant={employee.start ? "default" : "secondary"}
                    className={`${employee.start ? "bg-green-500" : "bg-gray-500"} text-xs px-2 py-0.5`}
                  >
                    {employee.start ? "Active" : "Inactive"}
                  </Badge>
                  {canManageEmployees && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="bg-white min-w-[180px]">
                        <DropdownMenuItem
                          onClick={() => handleViewEmployee(employee.id)}
                          className="cursor-pointer text-sm"
                        >
                          <Eye className="h-4 w-4 mr-2" />
                          View Details
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => { setSelectedEmployee(employee); setShowEditModal(true); }}
                          className="cursor-pointer text-sm"
                        >
                          <Edit className="h-4 w-4 mr-2" />
                          Edit Employee
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => handleStartStopEmployee(employee.id, employee.start, employee.is_overtime)}
                          disabled={loadingStates[employee.id]?.startStop}
                          className={`cursor-pointer text-sm ${employee.start ? "text-red-600" : "text-green-600"}`}
                        >
                          {loadingStates[employee.id]?.startStop ? (
                            <><Loader2 className="h-4 w-4 mr-2 animate-spin" />{employee.start ? "Stopping..." : "Starting..."}</>
                          ) : employee.start ? (
                            <><StopCircle className="h-4 w-4 mr-2" />Stop Recording</>
                          ) : (
                            <><Play className="h-4 w-4 mr-2" />Start Recording</>
                          )}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => handleOvertimeClick(employee.id, employee.is_overtime)}
                          disabled={loadingStates[employee.id]?.is_overtime}
                          className="cursor-pointer text-sm text-orange-600"
                        >
                          {loadingStates[employee.id]?.is_overtime ? (
                            <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Starting Overtime...</>
                          ) : (
                            <><Clock className="h-4 w-4 mr-2" />{employee.is_overtime ? "Stop Overtime" : "Start Overtime"}</>
                          )}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => handleDeleteEmployee(employee.id)}
                          disabled={loadingStates[employee.id]?.delete}
                          className="cursor-pointer text-sm text-red-600"
                        >
                          {loadingStates[employee.id]?.delete ? (
                            <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Deleting...</>
                          ) : (
                            <><Trash2 className="h-4 w-4 mr-2" />Delete Employee</>
                          )}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </div>
              </div>
              <div className="mt-2">
                <CardTitle className="text-base sm:text-lg leading-tight">
                  {employee.employeeName}
                </CardTitle>
                <CardDescription className="text-xs sm:text-sm mt-0.5">
                  {employee.shift || "No shift assigned"}
                </CardDescription>
              </div>
            </CardHeader>
            <CardContent className="space-y-2 px-4 pb-4">
              <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-600">
                <User className="h-3.5 w-3.5 sm:h-4 sm:w-4 flex-shrink-0" />
                <span className="truncate">{employee.employeeName}</span>
              </div>
              <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-600">
                <Key className="h-3.5 w-3.5 sm:h-4 sm:w-4 flex-shrink-0" />
                <span className="truncate">{employee.employeeID}</span>
              </div>
              <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-600">
                <ClockIcon className="h-3.5 w-3.5 sm:h-4 sm:w-4 flex-shrink-0" />
                <span className="truncate text-xs">
                  {employee.is_overtime
                    ? `Overtime${
                        formatOvertimeRange(employee)
                          ? ` (${formatOvertimeRange(employee)})`
                          : ""
                      }`
                    : `${employee.startTime} - ${employee.endTime}`}
                </span>
              </div>
              <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-600">
                <Monitor className="h-3.5 w-3.5 sm:h-4 sm:w-4 flex-shrink-0" />
                <span className="text-xs">
                  {employee.datatype === "screenshot" ? "Screenshot" : "Screen Recording"}
                </span>
              </div>
              <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-600">
                <Key className="h-3.5 w-3.5 sm:h-4 sm:w-4 flex-shrink-0" />
                <span className="text-xs">ID: {employee.loginID}</span>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* View Employee Modal */}
      <Dialog open={showViewModal} onOpenChange={setShowViewModal}>
        <DialogContent className="w-[95vw] max-w-3xl bg-white max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-lg sm:text-xl">Work Schedule Details</DialogTitle>
            <DialogDescription className="text-xs sm:text-sm">
              Complete information about {selectedEmployee?.employeeName}
            </DialogDescription>
          </DialogHeader>
          {selectedEmployee && <EmployeeDetails employee={selectedEmployee} />}
        </DialogContent>
      </Dialog>

      {/* Edit Employee Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="w-[95vw] max-w-6xl bg-white max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Employee</DialogTitle>
            <DialogDescription>Update employee information.</DialogDescription>
          </DialogHeader>
          {selectedEmployee && (
            <EmployeeForm employee={selectedEmployee} onSubmit={handleEditEmployee} employeeOptions={allOrgEmployees} />
          )}
        </DialogContent>
      </Dialog>

      {/* Overtime date-range Modal */}
      <Dialog open={!!overtimeDialogEmp} onOpenChange={(o) => { if (!o) setOvertimeDialogEmp(null); }}>
        <DialogContent className="w-[95vw] max-w-md bg-white">
          <DialogHeader>
            <DialogTitle>Start Overtime</DialogTitle>
            <DialogDescription>Select the date range overtime applies for this employee.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label className="text-sm">Start date</Label>
              <input
                type="date"
                value={otStartDate}
                min={todayStr}
                onChange={(e) => {
                  setOtStartDate(e.target.value);
                  if (otEndDate < e.target.value) setOtEndDate(e.target.value);
                }}
                className="w-full px-3 py-2 text-sm border rounded-md bg-background"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm">End date</Label>
              <input
                type="date"
                value={otEndDate}
                min={otStartDate}
                onChange={(e) => setOtEndDate(e.target.value)}
                className="w-full px-3 py-2 text-sm border rounded-md bg-background"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setOvertimeDialogEmp(null)}>Cancel</Button>
            <Button
              onClick={confirmStartOvertime}
              disabled={!!overtimeDialogEmp && loadingStates[overtimeDialogEmp]?.overtime}
            >
              {(!!overtimeDialogEmp && loadingStates[overtimeDialogEmp]?.overtime)
                ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" />Starting…</>)
                : "Start Overtime"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function EmployeeForm({
  employee,
  onSubmit,
  employeeOptions = [],
}: {
  employee?: UserType;
  onSubmit: (data: Partial<UserType>) => void;
  employeeOptions?: UserType[];
}) {
  // Display label for an employee in the picker: prefer full name, fall back to
  // first/last name, then to the employee ID itself.
  const employeeLabel = (emp: any) =>
    emp?.name ||
    [emp?.first_name, emp?.last_name].filter(Boolean).join(" ") ||
    emp?.employeeID ||
    "Unknown";
  const [formData, setFormData] = useState({
    startTime: employee?.startTime || "",
    endTime: employee?.endTime || "",
    shift: employee?.shift || "Day",
    interval: employee?.interval || 15,
    keyboardInterval: employee?.keyboardInterval || 15,
    department_id: employee?.department_id || "",
    emptID: employee?.emptID || "",
    employeeID: employee?.employeeID || "",
    start: false,
    stop: employee?.stop || false,
    weeks: employee?.weeks || [],
    sunday: employee?.weeks?.[0]?.sunday === undefined ? false : employee?.weeks?.[0]?.sunday,
    monday: employee?.weeks?.[0]?.monday === undefined ? true : employee?.weeks?.[0]?.monday,
    tuesday: employee?.weeks?.[0]?.tuesday === undefined ? true : employee?.weeks?.[0]?.tuesday,
    wednesday: employee?.weeks?.[0]?.wednesday === undefined ? true : employee?.weeks?.[0]?.wednesday,
    thursday: employee?.weeks?.[0]?.thursday === undefined ? true : employee?.weeks?.[0]?.thursday,
    friday: employee?.weeks?.[0]?.friday === undefined ? true : employee?.weeks?.[0]?.friday,
    saturday: employee?.weeks?.[0]?.saturday === undefined ? true : employee?.weeks?.[0]?.saturday,
    datatype: employee?.datatype || "screenshot",
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  // Default Start/End time from the branch office hours (/getOfficeDetail) for a
  // NEW schedule, so the tracking window matches the configured office timing
  // out of the box. Editing an existing schedule keeps its saved times. The
  // admin can still change these before saving — tracking uses whatever ends up
  // on the workmonitor schedule (returned by workmonitor_all).
  useEffect(() => {
    if (employee) return; // editing → keep the schedule's saved times
    let cancelled = false;
    (async () => {
      try {
        const res: any = await getOffice();
        const office = (res?.offices || [])[0];
        if (!office || cancelled) return;
        const toHHMM = (t: any) => (t ? String(t).slice(0, 5) : "");
        const s = toHHMM(office.working_hours_start);
        const e = toHHMM(office.working_hours_end);
        if (s || e) {
          setFormData((prev) => ({
            ...prev,
            startTime: prev.startTime || s,
            endTime: prev.endTime || e,
          }));
        }
      } catch {
        /* office hours are optional — leave the fields blank if unavailable */
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (formData.role !== "manager" && formData.department_id) {
      dispatch(getManagerList(formData.department_id));
    }
  }, [formData.role, formData.department_id]);

  useEffect(() => {
    dispatch(getDepartmentList());
  }, []);

  const departmentList = useSelector((state) => state.employee.departmentList);
  const managerList = useSelector((state) => state.employee.managerList);

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {/* Grid: 1 col on mobile, 2 on sm, 4 on lg */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div>
          <Label htmlFor="startTime" className="text-sm">Start Time</Label>
          <Input
            type="time"
            id="startTime"
            value={formData.startTime}
            onChange={(e) => setFormData({ ...formData, startTime: e.target.value })}
            required
            className="mt-1"
          />
        </div>
        <div>
          <Label htmlFor="endTime" className="text-sm">End Time</Label>
          <Input
            type="time"
            id="endTime"
            value={formData.endTime}
            onChange={(e) => setFormData({ ...formData, endTime: e.target.value })}
            className="mt-1"
          />
        </div>
        {/* Work Shift dropdown removed — shift defaults to "Day"; overtime is
            now controlled via the manual clock-in/out button, not this field. */}
        <div>
          <Label className="text-sm">Track Type</Label>
          <Select
            value={formData.datatype}
            onValueChange={(value) => setFormData({ ...formData, datatype: value })}
          >
            <SelectTrigger className="mt-1">
              <SelectValue placeholder="Select Track Type" />
            </SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="screenrecording">Screen Recording</SelectItem>
              <SelectItem value="screenshot">Screen Shot</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {formData.datatype === "screenshot" && (
          <div>
            <Label className="text-sm">Interval</Label>
            <Select
              value={Number(formData.interval)}
              onValueChange={(value) => setFormData({ ...formData, interval: value })}
            >
              <SelectTrigger className="mt-1">
                <SelectValue placeholder="Select Interval" />
              </SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value={5}>5 mins</SelectItem>
                <SelectItem value={10}>10 mins</SelectItem>
                <SelectItem value={15}>15 mins</SelectItem>
                <SelectItem value={30}>30 mins</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}
        <div>
          <Label className="text-sm">Activity Interval</Label>
          <Select
            value={Number(formData.keyboardInterval)}
            onValueChange={(value) => setFormData({ ...formData, keyboardInterval: value })}
          >
            <SelectTrigger className="mt-1">
              <SelectValue placeholder="Select Interval" />
            </SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value={3}>3 mins</SelectItem>
              <SelectItem value={5}>5 mins</SelectItem>
              <SelectItem value={10}>10 mins</SelectItem>
              <SelectItem value={15}>15 mins</SelectItem>
              <SelectItem value={30}>30 mins</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-sm">Employee</Label>
          <Select
            value={formData.employeeID}
            disabled={!!formData.emptID}
            onValueChange={(value) => setFormData({ ...formData, employeeID: value })}
          >
            <SelectTrigger className="mt-1">
              <SelectValue placeholder="Select Employee" />
            </SelectTrigger>
            <SelectContent className="bg-white">
              {employeeOptions.length === 0 ? (
                <div className="px-2 py-1.5 text-xs text-gray-500">
                  No employees available
                </div>
              ) : (
                employeeOptions.map((emp: any) => (
                  <SelectItem key={emp.id || emp.employeeID} value={emp.employeeID}>
                    {employeeLabel(emp)}
                  </SelectItem>
                ))
              )}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-sm">Department</Label>
          <Select
            value={formData.department_id}
            disabled={formData.employeeID}
            onValueChange={(value) => setFormData({ ...formData, department_id: value })}
          >
            <SelectTrigger className="mt-1">
              <SelectValue placeholder="Select department" />
            </SelectTrigger>
            <SelectContent className="bg-white">
              {departmentList.map((dept) => (
                <SelectItem key={dept.id} value={dept.id}>{dept.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-sm">Select Manager</Label>
          <Select
            value={formData.emptID}
            disabled={formData.employeeID}
            onValueChange={(value) => setFormData({ ...formData, emptID: value })}
          >
            <SelectTrigger className="mt-1">
              <SelectValue placeholder="Select Manager" />
            </SelectTrigger>
            <SelectContent className="bg-white">
              {managerList.map((e) => (
                <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Working Days */}
      <div>
        <h2 className="text-sm font-semibold mb-3">Working Days</h2>
        <div className="flex flex-wrap gap-3 sm:gap-4">
          {[
            { key: "monday", label: "Mon" },
            { key: "tuesday", label: "Tue" },
            { key: "wednesday", label: "Wed" },
            { key: "thursday", label: "Thu" },
            { key: "friday", label: "Fri" },
            { key: "saturday", label: "Sat" },
            { key: "sunday", label: "Sun" },
          ].map(({ key, label }) => (
            <div key={key} className="flex items-center space-x-1.5">
              <Checkbox
                id={key}
                checked={formData[key]}
                onCheckedChange={(checked) => setFormData({ ...formData, [key]: !!checked })}
              />
              <Label htmlFor={key} className="text-xs sm:text-sm font-medium cursor-pointer">
                {label}
              </Label>
            </div>
          ))}
        </div>
      </div>

      <div className="flex justify-end">
        <Button
          type="submit"
          className="bg-gradient-to-r from-blue-500 to-purple-600 text-sm"
        >
          {employee ? "Update" : "Start"} Schedule
        </Button>
      </div>
    </form>
  );
}

function EmployeeDetails({ employee }: { employee: UserType }) {
  return (
    <div className="space-y-4 text-sm">
      <div className="bg-gray-50 rounded-lg p-3 sm:p-4">
        <h3 className="font-semibold text-gray-700 mb-3 text-sm border-b pb-2">Personal Information</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {[
            { label: "Employee Name", value: employee.employeeName },
            { label: "Employee ID", value: employee.employeeID, mono: true },
            { label: "Login ID", value: employee.loginID, mono: true },
            { label: "Password", value: employee.password, mono: true },
          ].map(({ label, value, mono }) => (
            <div key={label}>
              <Label className="text-gray-500 text-xs">{label}</Label>
              <div className={`text-gray-800 mt-1 text-xs sm:text-sm break-all ${mono ? "font-mono" : "font-medium"}`}>
                {value || "N/A"}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-gray-50 rounded-lg p-3 sm:p-4">
        <h3 className="font-semibold text-gray-700 mb-3 text-sm border-b pb-2">Schedule & Tracking</h3>
        <div className="grid grid-cols-2 gap-3">
          {[
            { label: "Shift", value: employee.shift },
            { label: "Tracking Type", value: employee.datatype === "screenshot" ? "Screenshot" : "Screen Recording" },
            { label: "Start Time", value: employee.startTime },
            { label: "End Time", value: employee.endTime },
            ...(employee.datatype === "screenshot"
              ? [{ label: "Screenshot Interval", value: employee.interval ? `${employee.interval} min` : null }]
              : []),
            { label: "Activity Interval", value: employee.keyboardInterval ? `${employee.keyboardInterval} min` : null },
          ].map(({ label, value }) => (
            <div key={label}>
              <Label className="text-gray-500 text-xs">{label}</Label>
              <div className="text-gray-800 text-xs sm:text-sm mt-1">{value || "N/A"}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-gray-50 rounded-lg p-3 sm:p-4">
        <h3 className="font-semibold text-gray-700 mb-3 text-sm border-b pb-2">Status</h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <div>
            <Label className="text-gray-500 text-xs">Recording Status</Label>
            <div className="mt-1">
              <Badge className={`${employee.start ? "bg-green-500" : "bg-gray-500"} text-xs`}>
                {employee.start ? "Active" : "Inactive"}
              </Badge>
            </div>
          </div>
          <div>
            <Label className="text-gray-500 text-xs">Connection</Label>
            <div className="mt-1">
              <Badge className={`${employee.connected ? "bg-green-500" : "bg-red-500"} text-xs`}>
                {employee.connected ? "Connected" : "Disconnected"}
              </Badge>
            </div>
          </div>
          <div>
            <Label className="text-gray-500 text-xs">Overtime</Label>
            <div className="mt-1">
              <Badge className={`${employee.is_overtime ? "bg-orange-500" : "bg-gray-500"} text-xs`}>
                {employee.is_overtime ? "Active" : "Inactive"}
              </Badge>
              {employee.is_overtime && formatOvertimeRange(employee) && (
                <div className="text-[11px] text-gray-500 mt-1">
                  {formatOvertimeRange(employee)}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {employee.weeks?.[0] && (
        <div className="bg-gray-50 rounded-lg p-3 sm:p-4">
          <h3 className="font-semibold text-gray-700 mb-3 text-sm border-b pb-2">Working Days</h3>
          <div className="flex flex-wrap gap-2">
            {["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"].map(
              (day) =>
                employee.weeks[0][day] && (
                  <Badge key={day} className="bg-blue-500 text-xs capitalize">{day}</Badge>
                )
            )}
          </div>
        </div>
      )}
    </div>
  );
}